import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import MilestoneNotificationModal from '../Modals/MilestoneNotificationModal';

const { FiCalendar, FiHeart, FiStar, FiClock, FiSettings, FiCheck, FiChevronRight } = FiIcons;

const MilestoneCountdown = ({ milestones, name }) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [nextMilestone, setNextMilestone] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const calculateNextMilestone = () => {
    if (!milestones) return null;
    const today = new Date();
    const currentYear = today.getFullYear();

    const milestoneTypes = [
      { id: 'heavenlyBirthday', label: 'Heavenly Birthday', icon: FiStar, dateStr: milestones.heavenlyBirthday },
      { id: 'weddingAnniversary', label: 'Wedding Anniversary', icon: FiHeart, dateStr: milestones.weddingAnniversary },
      { id: 'memorialAnniversary', label: 'Memorial Anniversary', icon: FiCalendar, dateStr: milestones.memorialAnniversary },
    ];

    const upcoming = milestoneTypes
      .filter(m => m.dateStr)
      .map(m => {
        const [month, day] = m.dateStr.split('-').map(Number);
        let targetDate = new Date(currentYear, month - 1, day);
        
        if (targetDate < today && (today.getTime() - targetDate.getTime()) > 86400000) {
          targetDate = new Date(currentYear + 1, month - 1, day);
        }
        
        return { ...m, date: targetDate };
      })
      .sort((a, b) => a.date - b.date);

    return upcoming[0];
  };

  useEffect(() => {
    const next = calculateNextMilestone();
    setNextMilestone(next);

    const timer = setInterval(() => {
      if (!next) return;
      const now = new Date().getTime();
      const distance = next.date.getTime() - now;

      if (distance < 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [milestones]);

  if (!nextMilestone) return null;

  const isToday = timeLeft.days === 0 && new Date().getDate() === nextMilestone.date.getDate();

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-sm border border-brand-dark overflow-hidden relative group"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-accent-gold/5 rounded-bl-[120px] -mr-10 -mt-10 transition-transform group-hover:scale-110" />
        
        <div className="p-8 md:p-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-accent-gold/10 rounded-2xl flex items-center justify-center text-accent-gold shadow-sm">
              <SafeIcon icon={nextMilestone.icon} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-serif text-2xl text-accent-navy">{nextMilestone.label}</h3>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-text-muted">
                {nextMilestone.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>

          {isToday ? (
            <div className="bg-accent-gold text-white p-8 rounded-3xl text-center mb-8 shadow-xl shadow-accent-gold/20">
              <h4 className="text-3xl font-serif mb-2">Remembering {name.split(' ')[0]}</h4>
              <p className="text-sm font-bold uppercase tracking-widest opacity-80">Today is the Day</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3 mb-10">
              <CountdownUnit value={timeLeft.days} label="Days" />
              <CountdownUnit value={timeLeft.hours} label="Hrs" />
              <CountdownUnit value={timeLeft.minutes} label="Min" />
              <CountdownUnit value={timeLeft.seconds} label="Sec" />
            </div>
          )}

          <div className="space-y-4">
            <button 
              onClick={() => setIsModalOpen(true)}
              className="w-full btn-primary py-4 text-xs font-black uppercase tracking-widest flex items-center justify-center gap-3 group/btn shadow-lg shadow-accent-navy/10"
            >
              <SafeIcon icon={FiSettings} className="group-hover:rotate-90 transition-transform" /> Setup Notifications
            </button>
            <button 
              onClick={() => navigate('/resources?tab=Funeral Planning')}
              className="w-full group/btn flex items-center justify-between p-4 rounded-2xl border-2 border-brand-dark hover:border-accent-gold hover:bg-brand-light transition-all"
            >
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiClock} className="text-accent-gold" />
                <span className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Plan a Tribute</span>
              </div>
              <SafeIcon icon={FiChevronRight} className="text-text-muted group-hover/btn:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        <div className="bg-brand-light px-8 py-5 flex items-center gap-2 border-t border-brand-dark">
          <div className="flex -space-x-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="w-6 h-6 rounded-full border-2 border-white bg-brand-dark flex items-center justify-center text-[8px] font-bold text-accent-navy">
                {String.fromCharCode(64 + i)}
              </div>
            ))}
          </div>
          <p className="text-[9px] font-bold text-text-muted uppercase tracking-widest">
            14 others are honoring this cycle
          </p>
        </div>
      </motion.div>

      <MilestoneNotificationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        name={name}
        milestones={milestones}
      />
    </>
  );
};

const CountdownUnit = ({ value, label }) => (
  <div className="bg-brand-light rounded-2xl p-4 text-center border border-brand-dark group-hover:border-accent-gold/30 transition-colors">
    <div className="text-2xl md:text-3xl font-serif text-accent-navy mb-1 tabular-nums">
      {value.toString().padStart(2, '0')}
    </div>
    <div className="text-[8px] font-black uppercase tracking-widest text-text-muted opacity-60">
      {label}
    </div>
  </div>
);

export default MilestoneCountdown;